import React, { useCallback, useMemo, useState } from "react";
import classnames from "classnames";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { SearchInput } from "@whatslly/ui-components/v2/BaseElements/SearchInput";
import { useTranslation } from "react-i18next";
import escapeRegExp from "lodash/escapeRegExp";
import styles from "./search-messages-section.module.scss";
import type { ComponentMessageType } from "../../types/ComponentMessageType";

export const SearchMessagesSection: React.FC<{
  onSearchTextChange: (value: string) => void;
  onFocusMessageChange: (msgId: string) => void;
  elementBeforeSearch?: React.ReactNode;
  isLoading: boolean;
  messages: ComponentMessageType[];
  children?: React.ReactNode;
}> = ({ onFocusMessageChange, onSearchTextChange, children, elementBeforeSearch, isLoading, messages }) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [focusMessageId, setFocusMessageId] = useState<string>();

  const getMessagesWithSearchText = useCallback(
    (searchText: string) => {
      if (searchText && !isLoading && messages.length > 0) {
        return messages
          .filter((message) => {
            return (
              message.msg.match(new RegExp(`(${escapeRegExp(searchText)})`, `gi`)) ||
              (message.file?.transcribeText && message.file.transcribeText.match(new RegExp(`(${escapeRegExp(searchText)})`, `gi`)))
            );
          })
          .map((message) => message.msgId)
          .filter((id): id is string => Boolean(id))
          .reverse();
      }
      return [];
    },
    [isLoading, messages]
  );

  const numOfMessagesWithSearchText = useMemo(() => {
    return getMessagesWithSearchText(searchText).length;
  }, [getMessagesWithSearchText, searchText]);

  const currentFocusMessageIndex = useMemo(() => {
    const messages = getMessagesWithSearchText(searchText);
    return focusMessageId ? messages.indexOf(focusMessageId) : -1;
  }, [focusMessageId, getMessagesWithSearchText, searchText]);

  const scrollToMessage = useCallback(
    ({ indexDiff, currentSearchText }: { indexDiff?: number; currentSearchText?: string }) => {
      const msgs = getMessagesWithSearchText(currentSearchText ?? searchText);
      if (msgs.length === 0) {
        return;
      }
      const msgId = !focusMessageId || typeof indexDiff === "undefined" ? msgs[0] : msgs[msgs.indexOf(focusMessageId) + indexDiff];
      if (!msgId) {
        return;
      }
      setTimeout(() => {
        setFocusMessageId(msgId);
        onFocusMessageChange(msgId);
      });
    },
    [focusMessageId, getMessagesWithSearchText, onFocusMessageChange, searchText]
  );

  const scrollToNextMessage = useCallback(() => {
    scrollToMessage({ indexDiff: 1 });
  }, [scrollToMessage]);

  const scrollToPrevMessage = useCallback(() => {
    scrollToMessage({ indexDiff: -1 });
  }, [scrollToMessage]);

  const handleSearchTextChange = useCallback(
    (value: string) => {
      setSearchText(value);
      onSearchTextChange(value);
      scrollToMessage({ currentSearchText: value });
    },
    [onSearchTextChange, scrollToMessage]
  );

  return (
    <>
      <div className={styles.searchAndActionsContainer}>
        {elementBeforeSearch}
        <SearchInput
          placeholder={t("chat_component.search_input_placeholder")}
          className={styles.searchInput}
          onChange={handleSearchTextChange}
          value={searchText}
          isDisabled={isLoading}
          clearAllContainerClassName={styles.clearAllContainer}
          data-testid={`search-message-input`}
        />
        {children}
      </div>

      <div className={classnames(styles.searchInputButtons, { [styles.searchInputButtonsShow]: searchText })}>
        <div className={styles.searchInputButtonArrows}>
          <SvgV2
            data-testid="search-message-down-arrow"
            className={classnames({ [styles.searchInputButtonArrowDisabled]: numOfMessagesWithSearchText === 0 || currentFocusMessageIndex === 0 })}
            width={12}
            height={9}
            iconName={"arrow_down"}
            onClick={scrollToPrevMessage}
          />
          <SvgV2
            data-testid="search-message-up-arrow"
            className={classnames(styles.upArrow, {
              [styles.searchInputButtonArrowDisabled]:
                numOfMessagesWithSearchText === 0 || currentFocusMessageIndex + 1 === numOfMessagesWithSearchText,
            })}
            width={12}
            height={9}
            iconName={"arrow_down"}
            onClick={scrollToNextMessage}
          />
        </div>
        <div data-testid={`search-message-text`} className={styles.searchInputButtonText}>
          {t("chat_component.search_matches", { currentIndex: currentFocusMessageIndex + 1, numOfMatches: numOfMessagesWithSearchText })}
        </div>
      </div>
    </>
  );
};
